import React, { useState, useEffect } from "react";
import CountUp from "react-countup";
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import { Link } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { dashboardService } from "../../../services/dashboardService";
import { appointmentService } from "../../../services/appointmentService";
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Spinner from '../../../components/ui/Spinner';
import PendingSignaturesCard from './PendingSignaturesCard';
import MyAssistantsCard from './MyAssistantsCard';
import DelegatedSignaturesCard from './DelegatedSignaturesCard';
import {
  doctor_dashboard_01,
  doctor_dashboard_02,
  doctor_dashboard_03,
  morning_img_02,
  timericon,
} from "../../../components/imagepath";
import { parseApiDate } from '../../../lib/format';

const Doctor_Dashboard = () => {
  const { user } = useAuth();
  const doctorId = user?.doctor?.id ?? null;

  const [stats, setStats] = useState(null);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (doctorId) {
          const [doctorStats, today, upcoming] = await Promise.all([
            dashboardService.getDoctorStats(doctorId).catch(() => null),
            appointmentService.getToday(doctorId).catch(() => []),
            appointmentService.getUpcoming(5, doctorId).catch(() => []),
          ]);
          setStats(doctorStats);
          setTodayAppointments(Array.isArray(today) ? today : []);
          setUpcomingAppointments(Array.isArray(upcoming) ? upcoming : []);
        } else {
          // Admin/no-doctor fallback — global stats
          const [today, upcoming] = await Promise.all([
            appointmentService.getToday().catch(() => []),
            appointmentService.getUpcoming(5).catch(() => []),
          ]);
          setTodayAppointments(Array.isArray(today) ? today : []);
          setUpcomingAppointments(Array.isArray(upcoming) ? upcoming : []);
        }
      } catch (error) {
        console.error("Error loading dashboard:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [doctorId]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Buenos días";
    if (hour < 18) return "Buenas tardes";
    return "Buenas noches";
  };

  const formatTime = (time) => {
    if (!time) return "";
    return time.substring(0, 5);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return (parseApiDate(dateStr) ?? new Date(NaN)).toLocaleDateString("es-DO", {
      day: "2-digit",
      month: "short",
    });
  };

  const getNextAppointmentLabel = () => {
    if (!upcomingAppointments.length) return "—";
    const next = upcomingAppointments[0];
    const time = next.appointment_time || next.scheduled_time;
    if (!time) return "—";
    const [h, m] = time.split(":").map(Number);
    const apptTime = new Date();
    apptTime.setHours(h, m, 0, 0);
    const diffMs = apptTime - new Date();
    if (diffMs <= 0) return "En curso";
    const diffH = Math.floor(diffMs / 3600000);
    const diffM = Math.floor((diffMs % 3600000) / 60000);
    return `${String(diffH).padStart(2, "0")}h:${String(diffM).padStart(2, "0")}m`;
  };

  const doctorFullName = user?.doctor
    ? `${user.doctor.first_name} ${user.doctor.last_name}`
    : user?.name ?? "";

  const specialty = user?.doctor?.specialty?.name ?? null;

  return (
    <>
      {/* Breadcrumb */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Mi Panel' }]} />
              </div>
            </div>
          </div>

          {/* Greeting */}
          <div className="good-morning-blk">
            <div className="row">
              <div className="col-md-6">
                <div className="morning-user">
                  <h2>
                    {getGreeting()}, <span>Dr. {doctorFullName}</span>
                  </h2>
                  <p>
                    {specialty
                      ? `${specialty} — Que tenga un excelente día`
                      : "Que tenga un excelente día de trabajo"}
                  </p>
                </div>
              </div>
              <div className="col-md-6 position-blk">
                <div className="morning-img">
                  <img src={morning_img_02} alt="" />
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="doctor-list-blk">
            <div className="row">
              <div className="col-xl-3 col-md-6">
                <div className="doctor-widget border-right-bg">
                  <div className="doctor-box-icon flex-shrink-0">
                    <img src={doctor_dashboard_01} alt="" />
                  </div>
                  <div className="doctor-content dash-count flex-grow-1">
                    <h4>
                      <CountUp delay={0.4} end={stats?.today_appointments ?? todayAppointments.length} duration={0.6} />
                      <span className="status-green">Hoy</span>
                    </h4>
                    <h5>Mis Citas</h5>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6">
                <div className="doctor-widget border-right-bg">
                  <div className="doctor-box-icon flex-shrink-0">
                    <img src={doctor_dashboard_02} alt="" />
                  </div>
                  <div className="doctor-content dash-count flex-grow-1">
                    <h4>
                      <CountUp delay={0.4} end={stats?.today_consultations ?? 0} duration={0.6} />
                      <span className="status-green">Hoy</span>
                    </h4>
                    <h5>Mis Consultas</h5>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6">
                <div className="doctor-widget border-right-bg">
                  <div className="doctor-box-icon flex-shrink-0">
                    <img src={doctor_dashboard_03} alt="" />
                  </div>
                  <div className="doctor-content dash-count flex-grow-1">
                    <h4>
                      <CountUp delay={0.4} end={stats?.total_patients ?? 0} duration={0.6} />
                    </h4>
                    <h5>Mis Pacientes</h5>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-md-6">
                <div className="doctor-widget">
                  <div className="doctor-box-icon flex-shrink-0" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img src={timericon} alt="" style={{ width: 40, height: 40 }} />
                  </div>
                  <div className="doctor-content dash-count flex-grow-1">
                    <h4 style={{ fontSize: "1.1rem" }}>{getNextAppointmentLabel()}</h4>
                    <h5>Próxima Cita</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tables Row */}
          <div className="row">
            {/* Today's Appointments */}
            <div className="col-12 col-xl-6">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title d-inline-block">Citas de Hoy</h4>
                  <Link to="/appointments" className="patient-views float-end">Ver todas</Link>
                </div>
                <div className="card-body p-0 table-dash">
                  <div className="table-responsive">
                    <table className="table mb-0 border-0 custom-table">
                      <thead>
                        <tr>
                          <th>Paciente</th>
                          <th>Hora</th>
                          <th>Motivo</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr><td colSpan={4} className="text-center py-3"><Spinner /></td></tr>
                        ) : todayAppointments.length === 0 ? (
                          <tr><td colSpan={4} className="text-center text-muted py-3">Sin citas para hoy</td></tr>
                        ) : (
                          todayAppointments.slice(0, 6).map((appt) => (
                            <tr key={appt.id}>
                              <td>
                                {appt.patient
                                  ? `${appt.patient.first_name} ${appt.patient.last_name}`
                                  : "—"}
                              </td>
                              <td>{formatTime(appt.appointment_time || appt.scheduled_time)}</td>
                              <td className="text-muted">{appt.reason || "—"}</td>
                              <td>
                                <Link to={`/appointments/${appt.id}`} className="btn btn-sm btn-outline-primary">
                                  <FeatherIcon icon="eye" size={14} />
                                </Link>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Consultations */}
            <div className="col-12 col-xl-6">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title d-inline-block">Mis Últimas Consultas</h4>
                  <Link to="/consultations" className="patient-views float-end">Ver todas</Link>
                </div>
                <div className="card-body p-0 table-dash">
                  <div className="table-responsive">
                    <table className="table mb-0 border-0 custom-table">
                      <thead>
                        <tr>
                          <th>Paciente</th>
                          <th>Fecha</th>
                          <th>Motivo</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr><td colSpan={4} className="text-center py-3"><Spinner /></td></tr>
                        ) : !stats?.recent_consultations?.length ? (
                          <tr><td colSpan={4} className="text-center text-muted py-3">Sin consultas recientes</td></tr>
                        ) : (
                          stats.recent_consultations.map((c) => (
                            <tr key={c.id}>
                              <td>
                                {c.patient
                                  ? `${c.patient.first_name} ${c.patient.last_name}`
                                  : "—"}
                              </td>
                              <td>{formatDate(c.consultation_date)}</td>
                              <td className="text-muted" style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {c.chief_complaint || "—"}
                              </td>
                              <td>
                                <Link to={`/consultations/${c.id}`} className="btn btn-sm btn-outline-primary">
                                  <FeatherIcon icon="eye" size={14} />
                                </Link>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pending Signatures (clinical-record-signing R7) */}
          {doctorId && (
            <div className="row">
              <div className="col-12">
                <PendingSignaturesCard />
              </div>
            </div>
          )}

          {/* Delegated signing (clinical-delegation R7, clinical-record-signing R13) */}
          {doctorId && (
            <div className="row">
              <div className="col-12 col-xl-6">
                <MyAssistantsCard />
              </div>
              <div className="col-12 col-xl-6">
                <DelegatedSignaturesCard />
              </div>
            </div>
          )}

          {/* Upcoming Appointments */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title d-inline-block">Próximas Citas</h4>
                  <Link to="/appointments" className="patient-views float-end">Ver todas</Link>
                </div>
                <div className="card-body p-0 table-dash">
                  <div className="table-responsive">
                    <table className="table mb-0 border-0 custom-table">
                      <thead>
                        <tr>
                          <th>Paciente</th>
                          <th>Fecha</th>
                          <th>Hora</th>
                          <th>Motivo</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr><td colSpan={5} className="text-center py-3"><Spinner /></td></tr>
                        ) : upcomingAppointments.length === 0 ? (
                          <tr><td colSpan={5} className="text-center text-muted py-3">No hay citas próximas</td></tr>
                        ) : (
                          upcomingAppointments.map((appt) => (
                            <tr key={appt.id}>
                              <td>
                                {appt.patient
                                  ? `${appt.patient.first_name} ${appt.patient.last_name}`
                                  : "—"}
                              </td>
                              <td>{formatDate(appt.appointment_date || appt.scheduled_date)}</td>
                              <td>{formatTime(appt.appointment_time || appt.scheduled_time)}</td>
                              <td className="text-muted">{appt.reason || "—"}</td>
                              <td>
                                <Link to={`/appointments/${appt.id}`} className="btn btn-sm btn-outline-primary">
                                  <FeatherIcon icon="eye" size={14} />
                                </Link>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

    </>
  );
};

export default Doctor_Dashboard;
